import { Injectable, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';

import firebase from 'firebase/compat/app';

import { TerpecaRoom } from 'src/app/models/room.model';
import {
    ROOM_RESTRICTED_COLLECTION, RoomRestrictedFields, overlayRoomRestricted, splitRoomUpdate
} from 'src/app/utils/room-restricted.utils';

@Injectable({ providedIn: 'root' })
export class RoomRestrictedService {
  private db = inject(AngularFirestore);

  ref(roomId: string): firebase.firestore.DocumentReference {
    return this.db.firestore.collection(ROOM_RESTRICTED_COLLECTION).doc(roomId);
  }

  roomRef(roomId: string): firebase.firestore.DocumentReference {
    return this.db.firestore.collection('rooms').doc(roomId);
  }

  addToBatch(
      batch: firebase.firestore.WriteBatch, roomId: string, update: Partial<TerpecaRoom>,
      createPublic = false): void {
    const { publicUpdate, restrictedUpdate } = splitRoomUpdate(update);
    if (createPublic) {
      batch.set(this.roomRef(roomId), publicUpdate);
    } else if (Object.keys(publicUpdate).length > 0) {
      batch.update(this.roomRef(roomId), publicUpdate);
    }
    if (Object.keys(restrictedUpdate).length > 0) {
      batch.set(this.ref(roomId), restrictedUpdate, { merge: true });
    }
  }

  async updateRoom(roomId: string, update: Partial<TerpecaRoom>): Promise<void> {
    const batch = this.db.firestore.batch();
    this.addToBatch(batch, roomId, update);
    await batch.commit();
  }

  async loadMap(): Promise<Map<string, RoomRestrictedFields>> {
    const snapshot = await this.db.collection<RoomRestrictedFields>(ROOM_RESTRICTED_COLLECTION).ref.get();
    const byId = new Map<string, RoomRestrictedFields>();
    for (const doc of snapshot.docs) {
      byId.set(doc.id, <RoomRestrictedFields>(doc.data() || {}));
    }
    return byId;
  }

  overlay(rooms: TerpecaRoom[], restrictedById: Map<string, RoomRestrictedFields>): TerpecaRoom[] {
    return rooms.map(room => overlayRoomRestricted(room, restrictedById.get(room.docId || '')));
  }

  async overlayLoadedRooms(rooms: TerpecaRoom[]): Promise<TerpecaRoom[]> {
    if (!rooms.length) {
      return rooms;
    }
    return this.overlay(rooms, await this.loadMap());
  }
}
